import type { SnapValue } from '@/domain/types';
import { type NormalisedSignatureMap, signatureAt, ticksPerBar, ticksPerBeat } from './tempoMap';

/**
 * Snapping is computed in ticks, not pixels, so that the same grid applies at
 * every zoom level and in every editor (arrangement, piano roll, automation).
 */

export const SNAP_VALUES: readonly SnapValue[] = ['off', 'bar', '1/1', '1/2', '1/4', '1/8', '1/16', '1/32'];

export const SNAP_LABELS: Record<SnapValue, string> = {
  off: 'Off',
  bar: 'Bar',
  '1/1': 'Whole',
  '1/2': '1/2',
  '1/4': '1/4',
  '1/8': '1/8',
  '1/16': '1/16',
  '1/32': '1/32',
};

/** Grid step in ticks for a snap value at a given position. 0 means "no grid". */
export function snapStepTicks(map: NormalisedSignatureMap, snap: SnapValue, tick: number): number {
  if (snap === 'off') return 0;
  const sig = signatureAt(map, tick);
  if (snap === 'bar') return ticksPerBar(sig, map.ppq);
  const beat = ticksPerBeat(sig, map.ppq);
  switch (snap) {
    case '1/1':
      return beat * 4;
    case '1/2':
      return beat * 2;
    case '1/4':
      return beat;
    case '1/8':
      return beat / 2;
    case '1/16':
      return beat / 4;
    case '1/32':
      return beat / 8;
    default:
      return beat;
  }
}

export type SnapDirection = 'nearest' | 'floor' | 'ceil';

export function snapTick(
  map: NormalisedSignatureMap,
  snap: SnapValue,
  tick: number,
  direction: SnapDirection = 'nearest',
): number {
  const step = snapStepTicks(map, snap, tick);
  if (step <= 0) return Math.max(0, Math.round(tick));
  const sig = signatureAt(map, tick);
  const origin = sig.tick;
  const rel = tick - origin;
  const q =
    direction === 'floor'
      ? Math.floor(rel / step)
      : direction === 'ceil'
        ? Math.ceil(rel / step)
        : Math.round(rel / step);
  return Math.max(0, Math.round(origin + q * step));
}

/**
 * Snap a delta rather than an absolute position. Used while dragging, so a clip
 * keeps its offset from the grid instead of being yanked onto it.
 */
export function snapDelta(
  map: NormalisedSignatureMap,
  snap: SnapValue,
  anchorTick: number,
  deltaTicks: number,
): number {
  const step = snapStepTicks(map, snap, anchorTick + deltaTicks);
  if (step <= 0) return Math.round(deltaTicks);
  const target = anchorTick + deltaTicks;
  return snapTick(map, snap, target) - anchorTick;
}
